import { useApi } from "@/hooks/useApi";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import { stripSuggestionPrefix } from "@/utils/text";
import { SearchQueriesSuggestion } from "@shaarp/common";
import {
  BuildingIcon,
  Loader2Icon,
  RefreshCwIcon,
  StarsIcon,
  UserIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Suggestion } from "../ai-elements/suggestion";
import { useToastError } from "../ui/use-toast";

export const SearchQueriesAiSuggestions = ({
  onSelect = () => {},
  onSuggestionsLoaded,
}: {
  onSelect: (query: string) => void;
  onSuggestionsLoaded?: (queries: string[]) => void;
}) => {
  const api = useApi();
  const toastError = useToastError();
  const { t } = useTranslation();
  const { isMobile } = useDeviceFormat();

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [suggestions, setSuggestions] = useState<SearchQueriesSuggestion[]>([]);
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    fetchSearchCompanies();
  }, []);

  const fetchSearchCompanies = async (exclude: string[] = []) => {
    try {
      const { data } = await api.get<SearchQueriesSuggestion[]>(
        `/my/team/ai-suggestions/search-queries-suggestions`,
        {
          params: {
            exclude,
          },
          paramsSerializer: { indexes: null },
        }
      );

      // The server answers with an empty list rather than an error when it
      // cannot build a pool. On the first load that hides the row, which is
      // what it should do; on "other suggestions" it would wipe what the user
      // is reading, so the current set stays put instead.
      if (!data.length) {
        return;
      }

      setSuggestions(data);
      setAnimationKey((prev) => prev + 1);
      onSuggestionsLoaded?.(data.map((s) => s.query));
    } catch (error) {
      toastError({
        title: t("prospecting.searchQueriesSuggestions.error.title"),
        description: (error as Error).message,
      });
    }
  };

  // Draws a new set from the server-side pool instead of forcing a fresh
  // generation. Passing what is on screen makes the server skip it, so the
  // click returns genuinely different suggestions — instantly.
  const loadMoreSuggestions = async () => {
    setIsLoadingMore(true);
    await fetchSearchCompanies(suggestions.map((s) => s.query));
    setIsLoadingMore(false);
  };

  if (!suggestions.length) {
    return null;
  }

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      <p className="text-sm text-muted-foreground text-center">
        <StarsIcon className="inline-block w-3.5 h-3.5 mr-1 -mt-0.5" />
        {t("prospecting.searchQueriesSuggestions.contextLine")}
      </p>
      <div
        key={`suggestions-${animationKey}`}
        className={
          isMobile
            ? "flex flex-col gap-2 w-full"
            : "flex flex-wrap gap-2 justify-center w-full"
        }
      >
        {suggestions.map((suggestion, index) => (
          <Suggestion
            key={suggestion.query}
            onClick={() => onSelect(suggestion.query)}
            suggestion={suggestion.query}
            className={`suggestion-cascade ${
              isMobile ? "justify-start py-3 w-full h-auto" : ""
            }`}
            style={{
              animationDelay: `${index * 100}ms`,
            }}
          >
            {suggestion.type === "company" ? (
              <BuildingIcon className="shrink-0 mt-0.5" />
            ) : (
              <UserIcon className="shrink-0 mt-0.5" />
            )}
            <span className={isMobile ? "text-left whitespace-normal" : ""}>
              {stripSuggestionPrefix(suggestion.query)}
            </span>
          </Suggestion>
        ))}
        <Suggestion
          key={`other-suggestions-${animationKey}`}
          onClick={loadMoreSuggestions}
          suggestion="other-suggestions"
          className={`suggestion-cascade ${
            isMobile ? "justify-center w-full" : ""
          }`}
          disabled={isLoadingMore}
          style={{
            animationDelay: `${suggestions.length * 100}ms`,
          }}
        >
          {t("prospecting.searchQueriesSuggestions.more")}
          {isLoadingMore ? (
            <Loader2Icon className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCwIcon className="w-4 h-4" />
          )}
        </Suggestion>
      </div>
    </div>
  );
};
